/**
 * IncomingKeypad.js
 * Talkroute
 *
 * Created by Vel Genov on 1/21/20
 * Copyright (c) 2020 Talkroute. All right reserved.
 */

import React, { useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { Grid } from "semantic-ui-react";

import KeyComponent, { Types } from "./KeyComponent";
import { formatPhoneNumber } from "../../services/dataClasses/CallData";
import uiString from "../../constants/uiString";
import {
  hangUp,
  accept,
  setIsKeypadVisible,
  setIsMuteMic,
} from "../../actions/callActions";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { GET_TEN_THOUSAND_CONTACTS, CONTACTS_INITIAL_PAGE } from "@components/Contacts/constants";
import { findFirstContactByNumberFromList } from "@helpers/functions/contacts";
import { useGetAllContactsQuery } from "@services/kingcobraApi";

/**
 * IncomingKeypad component
 */
const IncomingKeypad = () => {
  const incomingCallNumber = useAppSelector( state => state.callReducer.incomingCallNumber)
  const dialedNumber = useAppSelector( state => state.callReducer.dialedNumber)
  const isKeypadVisible = useAppSelector( state => state.callReducer.isKeypadVisible)
  const isMuteMic = useAppSelector( state => state.callReducer.isMuteMic)
  const dispatch = useDispatch();

  const {data: _contacts, } = useGetAllContactsQuery({ params: { 
    pageSize: GET_TEN_THOUSAND_CONTACTS,
    page: CONTACTS_INITIAL_PAGE
  } });
  
  const contacts = useMemo(() => _contacts || [], [_contacts]);
  const contact = useMemo(() => incomingCallNumber ? findFirstContactByNumberFromList(incomingCallNumber, contacts) : null, [incomingCallNumber, contacts]);
  
  
  /**
   * Display the Incoming phone number field
   */
  const showIncomingPhoneNumber = () => {

    if (incomingCallNumber) {
      const formattedPhoneNumber = contact ? contact.name : formatPhoneNumber(incomingCallNumber);
      return `${uiString.calls.INCOMING} ${formattedPhoneNumber}`;
    }

    return uiString.calls.UNKNOWN;
  };

  /**
   * Display the Dialed phone number field
   */
  const showDialedPhoneNumber = () => {

    if (dialedNumber) {
      const formattedPhoneNumber = formatPhoneNumber(dialedNumber);

      return `${uiString.calls.DIALED} ${formattedPhoneNumber}`;
    }

    return uiString.calls.UNKNOWN;
  };

  /**
   * Process a click on the end call button
   */
  const onEndCallButtonClick = () => dispatch(hangUp());

  const onAcceptCallButtonClick = () => dispatch(accept());

  /**
   * Process a click on the toggle keypad button
   */
  const onToggleKeypad = () => dispatch(setIsKeypadVisible(!isKeypadVisible))

  /**
   * Process a click on the mute microphone button
   */
  const onMuteMic = () => dispatch(setIsMuteMic(!isMuteMic))

    const buttonType = isKeypadVisible ? Types.text : Types.blank;

    return (
      <Grid
        textAlign="center"
        verticalAlign="middle"
        style={{
          height: "100%",
          width: "100%",
          margin: "0px",
          backgroundColor: "var(--color-background-grey)",
        }}
      >
        <Grid.Column style={{ padding: "0px" }}>
          <div
            className="text-normal none-select-text"
            style={{
              textAlign: "center",
              fontSize: "25px",
              lineHeight: "25px",
              paddingTop: "20px",
              height: "40px",
            }}
          >
            {showIncomingPhoneNumber()}
          </div>
          <div
            className="text-normal none-select-text"
            style={{
              textAlign: "center",
              fontSize: "18px",
              padding: "10px 0 20px 0",
              height: "46px",
            }}
          >
            {showDialedPhoneNumber()}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <div className="keypad_container">
              {/* Row 1 */}
              <KeyComponent keyTitle="1" type={buttonType} />
              <KeyComponent keyTitle="2" keySubtitle="ABC" type={buttonType} />
              <KeyComponent keyTitle="3" keySubtitle="DEF" type={buttonType} />

              {/* Row 2 */}

              <KeyComponent keyTitle="4" keySubtitle="GHI" type={buttonType} />
              <KeyComponent keyTitle="5" keySubtitle="JKL" type={buttonType} />
              <KeyComponent keyTitle="6" keySubtitle="MNO" type={buttonType} />

              {/* Row 3 */}

              <KeyComponent keyTitle="7" keySubtitle="PQRS" type={buttonType} />
              <KeyComponent keyTitle="8" keySubtitle="TUV" type={buttonType} />
              <KeyComponent keyTitle="9" keySubtitle="WXYZ" type={buttonType} />

              {/* Row 4 */}

              <KeyComponent keyTitle="*" type={buttonType} />
              <KeyComponent keyTitle="0" keySubtitle="+" type={buttonType} />
              <KeyComponent keyTitle="#" type={buttonType} />

              {/* Row 5 */}

              <KeyComponent
                type={Types.end}
                clickActionCallback={() => onEndCallButtonClick()}
              />
              <KeyComponent type={Types.blank} />
              <KeyComponent
                type={Types.answer}
                clickActionCallback={() => onAcceptCallButtonClick()}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>
    );
}

export default IncomingKeypad;
